import type { ReactElement } from 'react';

import { useApp } from '@weke/ui';
import { formatMoney, formatPercentBp, formatSignedMoney } from '@weke/ui';
import { Panel } from '@weke/ui';

/**
 * The account panel: the server's numbers, with the margin-call level visible.
 *
 * Every figure is from `POST /preview` (`tryAssessAccount`): balance, equity,
 * floating P&L, used margin, free margin and margin level. The margin-call and
 * stop-out levels are the group's configuration, also from the server, and the
 * level is rendered **with its threshold** — a bare `214%` tells a trader nothing
 * about how close they are to trouble.
 *
 * When the book cannot be valued (a stale or missing mark) the panel says so
 * instead of showing a balance-shaped equity.
 */
export function AccountPanel(): ReactElement {
  const app = useApp();
  const account = app.account;
  const digits = account?.digits ?? 2;
  const currency = account?.currency ?? 'USD';

  if (account === null) {
    return (
      <Panel id="account" title="Account">
        <p className="muted p-3 text-xs">Waiting for the first server snapshot…</p>
      </Panel>
    );
  }

  const metric = (label: string, value: string, tone?: string, testId?: string): ReactElement => (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="label">{label}</dt>
      <dd className={`num text-sm ${tone ?? ''}`} data-testid={testId}>
        {value}
      </dd>
    </div>
  );

  const level = account.margin_level_bp;
  const callLevel = account.margin_call_level_bp;
  const atRisk =
    level !== null && callLevel !== null && BigInt(level) <= BigInt(callLevel);

  return (
    <Panel id="account" title="Account" badge={`${currency} · login ${account.login}`}>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 p-2">
        <dl className="flex flex-col gap-1">
          {metric('Balance', formatMoney(account.balance_minor, digits), '', 'account-balance')}
          {metric('Equity', formatMoney(account.equity_minor, digits), '', 'account-equity')}
          {metric(
            'Floating P&L',
            formatSignedMoney(account.floating_minor, digits),
            account.floating_minor.startsWith('-') ? 'loss' : account.floating_minor === '0' ? 'muted' : 'profit',
          )}
        </dl>
        <dl className="flex flex-col gap-1">
          {metric('Used margin', formatMoney(account.used_margin_minor, digits), '', 'account-used-margin')}
          {metric('Free margin', formatMoney(account.free_margin_minor, digits), '', 'account-free-margin')}
          {metric(
            'Margin level',
            formatPercentBp(level),
            atRisk ? 'loss' : undefined,
            'account-margin-level',
          )}
        </dl>
      </div>
      <div className="border-t border-line px-2 py-1.5">
        <div className="flex items-center justify-between text-2xs">
          <span className="muted">
            Margin call at {formatPercentBp(account.margin_call_level_bp)} · stop out at{' '}
            {formatPercentBp(account.stop_out_level_bp)}
          </span>
          {atRisk ? (
            <span className="loss" role="status">
              {account.margin_call ? '⚠ Margin call' : '⚠ Below call level'}
            </span>
          ) : (
            <span className="faint">
              {account.stop_out ? 'Stop-out level' : 'Above call level'}
            </span>
          )}
        </div>
        {account.valuable ? null : (
          <p className="mt-1 text-2xs text-warning" role="status">
            Cannot value the book: {account.valuation_issue}. Equity shows the balance only.
          </p>
        )}
      </div>
    </Panel>
  );
}
